import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { motion, AnimatePresence } from 'framer-motion';
import { useWeb3 } from '../../context/Web3Context';
import { useToast } from '../../context/ToastContext';
import CONTRACTS from '../../contracts/addresses';
import DashboardLoader from '../Loading/DashboardLoader';
import { AnimVal, MCard, PageWrap, Confetti, ROITimer } from '../UI';
import { formatUSDT } from '../../utils/helpers';

const AMTS = [10, 20, 30, 50, 100, 200, 300, 500, 1000];

export default function InvestPage() {
  const { contracts, userId, account } = useWeb3();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [usdtBal, setUsdtBal] = useState(null);
  const [totalInv, setTotalInv] = useState(null);
  const [invs, setInvs] = useState([]);
  const [amt, setAmt] = useState('');
  const [custom, setCustom] = useState('');
  const [investing, setInvesting] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [timeUnit, setTimeUnit] = useState(600); // default 10min, will be overridden

  useEffect(() => {
    if (!contracts.storage || !userId) return;
    (async () => {
      try {
        // Read TIME_UNIT from contract
        const tu = await contracts.storage.TIME_UNIT();
        setTimeUnit(tu.toNumber());

        const [bal, ui, count] = await Promise.all([
          contracts.usdt.balanceOf(account), contracts.storage.getUserInfo(userId), contracts.storage.getInvestmentCount(userId)
        ]);
        setUsdtBal(bal); setTotalInv(ui[3]);
        const list = [];
        const c = count.toNumber();
        for (let i = 0; i < c; i++) {
          const inv = await contracts.storage.getInvestment(userId, i);
          list.push({ idx: i, amount: inv[0], ts: inv[1], roiE: inv[2], totalE: inv[3], cap: inv[4], rem: inv[5], active: inv[6], lastROI: inv[7] });
        }
        setInvs(list.reverse());
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [contracts.storage, userId, account]);

  const finalAmt = custom || amt;
  const handleInvest = async () => {
    if (!finalAmt || parseFloat(finalAmt) < 10 || parseFloat(finalAmt) % 10 !== 0) { toast.error('Min $10, multiples of $10'); return; }
    setInvesting(true);
    try {
      const wei = ethers.utils.parseUnits(finalAmt.toString(), 18);
      const allowance = await contracts.usdt.allowance(account, CONTRACTS.STORAGE);
      if (allowance.lt(wei)) { toast.info('Approving...'); const atx = await contracts.usdt.approve(CONTRACTS.STORAGE, ethers.constants.MaxUint256); await atx.wait(); }
      toast.info('Investing...');
      const tx = await contracts.logic.invest(wei);
      await tx.wait();
      toast.success('Investment successful! 🎉');
      setConfetti(true);
      setTimeout(() => window.location.reload(), 3000);
    } catch (e) { toast.error(e.reason || 'Failed'); }
    setInvesting(false);
  };

  if (loading) return <DashboardLoader />;

  return (<PageWrap>
    <Confetti active={confetti} />
    <MCard className="hero-card" delay={0}>
      <div className="g2">
        <div className="fcc"><AnimVal value={usdtBal || 0} className="val val-lg val-cyan" /><div className="label">USDT Balance</div></div>
        <div className="fcc"><AnimVal value={totalInv || 0} className="val val-lg val-gold" /><div className="label">Total Invested</div></div>
      </div>
    </MCard>

    <div className="stitle">New Investment</div>
    <MCard className="conic-card" delay={0.08}>
      <div className="g3" style={{ marginBottom: 12 }}>
        {AMTS.map(a => (
          <motion.div key={a} className={`amt-btn ${!custom && amt === String(a) ? 'on' : ''}`}
            onClick={() => { setAmt(String(a)); setCustom(''); }} whileTap={{ scale: 0.92 }}>${a}</motion.div>
        ))}
      </div>
      <div className="fg"><input type="number" placeholder="Custom amount (multiples of $10)" value={custom} onChange={e => { setCustom(e.target.value); setAmt(''); }} /></div>
      <div style={{ fontSize: '0.7rem', color: 'var(--text3)', marginBottom: 14, lineHeight: 1.6 }}></div>
      <motion.button className="btn btn-gold btn-full" onClick={handleInvest} disabled={investing} style={{ padding: 16 }}
        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
        {investing ? '⏳ Processing...' : `💰 Invest $${finalAmt || '0'}`}
      </motion.button>
    </MCard>

    <div className="stitle">Investment History</div>
    {invs.length === 0 ? <MCard delay={0.2}><p style={{ textAlign: 'center', color: 'var(--text3)', padding: 16 }}>No investments yet</p></MCard> :
      invs.map((inv, i) => (
        <MCard key={i} delay={0.16 + i * 0.06}>
          <div className="fb">
            <AnimVal value={inv.amount} className="val val-md" />
            <span className={`badge ${inv.active ? 'badge-gr' : 'badge-r'}`}>{inv.active ? '● Active' : '● Capped'}</span>
          </div>
          {/* ROI Timer per investment */}
          {inv.active && (
            <div style={{ marginTop: 12, marginBottom: 12, padding: '12px', background: 'rgba(0,245,255,0.03)', borderRadius: 12, border: '1px solid rgba(0,245,255,0.08)' }}>
              <ROITimer lastROITimestamp={inv.lastROI.toNumber()} timeUnit={timeUnit} />
            </div>
          )}
          <div className="divider" />
          <div className="g3">
            <div className="fcc"><AnimVal value={inv.totalE} className="val val-sm val-cyan" /><div className="label">Earned</div></div>
            <div className="fcc"><AnimVal value={inv.cap} className="val val-sm val-gold" /><div className="label">Cap</div></div>
            <div className="fcc"><AnimVal value={inv.rem} className="val val-sm" /><div className="label">Remaining</div></div>
          </div>
        </MCard>
      ))}
  </PageWrap>);
}
